/** Configuration hub — единая точка входа для всех модулей конфигурации.
 *
 * По образцу Saleor Dashboard Configuration page (grid из тематических групп).
 */

import { Link } from "react-router-dom";
import {
  Settings as SettingsIcon,
  Users,
  ShoppingBag,
  Layers,
  Tag,
  FileText,
  Megaphone,
  Ticket,
  Webhook,
  Store,
  FileBox,
  Building2,
  Menu as MenuIcon,
  Rows3 as FooterIcon,
  Palette,
  Upload,
  Bot,
} from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";

interface ConfigItem {
  to: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string;
}

interface ConfigGroup {
  title: string;
  items: ConfigItem[];
}

const GROUPS: ConfigGroup[] = [
  {
    title: "Каталог",
    items: [
      {
        to: "/configuration/product-types",
        title: "Типы товаров",
        description:
          "Шаблоны товаров (Холодильник / ТВ / Пылесос) с набором атрибутов",
        icon: Layers,
        badge: "MVP",
      },
      {
        to: "/configuration/attributes",
        title: "Атрибуты",
        description:
          "Характеристики (Цвет, Материал, Габариты) с values для dropdown",
        icon: Layers,
        badge: "MVP",
      },
      {
        to: "/configuration/channels",
        title: "Каналы продаж",
        description:
          "Multichannel-каналы (Розница / B2B / маркетплейсы) — пока 1 default",
        icon: Building2,
        badge: "MVP",
      },
      {
        to: "/configuration/brands",
        title: "Бренды",
        description:
          "Справочник брендов (logo + slug) для CMS-блока brand_strip",
        icon: Tag,
      },
      {
        to: "/configuration/catalog-import",
        title: "Импорт каталога",
        description:
          "Bulk-импорт товаров с Texnomart.uz по категориям — импортированные товары скрыты до ручной проверки",
        icon: Upload,
        badge: "NEW",
      },
      {
        to: "/configuration/ai-content",
        title: "ИИ-контент",
        description:
          "Агент карточек товаров: загрузите остатки из 1С — ИИ найдёт товары в интернете и создаст черновики с описаниями, фото и ценами",
        icon: Bot,
        badge: "NEW",
      },
    ],
  },
  {
    title: "Контент и магазины",
    items: [
      {
        to: "/configuration/pages",
        title: "Страницы (CMS)",
        description:
          "Блочный редактор лендингов, FAQ, info-страниц (PageType + блоки)",
        icon: FileText,
        badge: "MVP",
      },
      {
        to: "/configuration/footer",
        title: "⭐ Футер сайта (всё в одном)",
        description:
          "Единая страница: newsletter, колонки ссылок, QR с подписями, кнопки сторов, соцсети, legal-текст и ссылки",
        icon: FooterIcon,
        badge: "NEW",
      },
      {
        to: "/configuration/navigation",
        title: "Меню Header (low-level)",
        description:
          "Сырое управление навигацией: header_top, header_quick, mobile_menu. Футер настраивается на отдельной странице.",
        icon: MenuIcon,
      },
      {
        to: "/site-content",
        title: "Контент сайта",
        description: "Hero, About, Footer, Delivery — единый CMS-tab",
        icon: FileBox,
      },
      {
        to: "/configuration/design-tokens",
        title: "⭐ Brand look (design tokens)",
        description:
          "Цвета, шрифты, радиусы, spacing — единый SSOT для web/TMA/mobile. Меняй здесь → все три фронта обновляются.",
        icon: Palette,
        badge: "NEW",
      },
      {
        to: "/stores",
        title: "Магазины",
        description: "Offline-точки с GPS-координатами для /stores на сайте",
        icon: Store,
      },
    ],
  },
  {
    title: "Маркетинг",
    items: [
      {
        to: "/configuration/promotions",
        title: "Promotions (auto-discounts)",
        description:
          "Автоматические скидки с predicate tree (AND/OR условия на товары/категории/сумму)",
        icon: Megaphone,
        badge: "MVP",
      },
      {
        to: "/configuration/vouchers",
        title: "Vouchers (промокоды)",
        description:
          "Multiple codes per voucher, лимиты per-customer, для SMS/TG-кампаний",
        icon: Ticket,
        badge: "MVP",
      },
      {
        to: "/promo-codes",
        title: "Promo Codes (legacy)",
        description: "Старая система промокодов — будет заменена Vouchers",
        icon: Tag,
      },
    ],
  },
  {
    title: "Staff и доступ",
    items: [
      {
        to: "/configuration/staff-users",
        title: "Сотрудники",
        description:
          "Создание аккаунтов для входа в админку и назначение им групп прав",
        icon: Users,
        badge: "NEW",
      },
      {
        to: "/configuration/staff-groups",
        title: "Группы прав (RBAC)",
        description:
          "Гранулярные permissions: MANAGE_PRODUCTS, MANAGE_ORDERS, ...",
        icon: Users,
        badge: "MVP",
      },
    ],
  },
  {
    title: "Заказы",
    items: [
      {
        to: "/orders",
        title: "Заказы",
        description: "Order timeline (events) теперь доступен в деталях заказа",
        icon: ShoppingBag,
      },
      {
        to: "/configuration/refunds",
        title: "Возвраты",
        description: "Двухэтапные refund'ы: pending → granted → sent (Phase C2)",
        icon: FileBox,
        badge: "MVP",
      },
    ],
  },
  {
    title: "Интеграции",
    items: [
      {
        to: "/configuration/webhooks",
        title: "Webhook Subscriptions",
        description:
          "Outbound webhooks на внешние системы (Bitrix, Eskiz, custom)",
        icon: Webhook,
        badge: "MVP",
      },
      {
        to: "/settings",
        title: "Общие настройки",
        description:
          "Контакты, доставка, оплата, Bitrix, бренд — старая Settings page",
        icon: SettingsIcon,
      },
    ],
  },
];

export function ConfigurationPage(): JSX.Element {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading">Конфигурация</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Управление каталогом, контентом, маркетингом, доступами и интеграциями
        </p>
      </div>

      {GROUPS.map((group) => (
        <section key={group.title} className="space-y-3">
          <h2 className="text-sm uppercase tracking-wider text-muted-foreground font-medium">
            {group.title}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {group.items.map((item) => {
              const Icon = item.icon;
              return (
                <Link key={item.to} to={item.to} className="group">
                  <Card className="h-full transition-shadow hover:shadow-md hover:border-primary/30">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="rounded-lg bg-primary/10 p-2 text-primary shrink-0">
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-sm group-hover:text-primary truncate">
                              {item.title}
                            </h3>
                            {item.badge && (
                              <span className="text-[10px] uppercase rounded bg-warning/15 text-warning px-1.5 py-0.5 font-semibold">
                                {item.badge}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {item.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
